import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { DndProvider, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Paper,
  Modal,
  
  IconButton,
  Tooltip,
  Badge,
  Grid,
  Divider,
  Avatar,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  FilterList as FilterIcon,

  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Alarm as AlarmIcon,
  Dashboard as DashboardIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { getToken,base_url } from "../../config/config.js";
import TaskFormModal from "./KanbanComponents/TaskFormModal";
import TaskCard from "./KanbanComponents/TaskCard";


const ItemTypes = {
  TASK: "task",
};


  /***************************************
   * Theme Configuration
   ***************************************/
  const lightThemeColors = {
    primary: "#4361ee",
    secondary: "#3a0ca3",
    success: "#2ecc71",
    warning: "#f39c12",
    error: "#e74c3c",
    info: "#3498db",
    background: "#f8f9fd",
    card: "#ffffff",
    text: "#2d3748",
    textSecondary: "#718096",
    columnHeader: "#ebf3ff",
    columnBackground: "#ffffff",
    columnBackgroundHover: "#f7faff",
    taskBacklog: "#ffffff",
    taskInProgress: "#fff8f0",
    taskDone: "#f0fff4",
    taskBorder: "#e2e8f0",
    taskShadow: "0 4px 6px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.08)",
    taskShadowHover: "0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.08)",
    headerBackground: "#ffffff",
    headerShadow: "0 1px 3px rgba(0,0,0,0.05)",
  };
  
  const darkThemeColors = {
    primary: "#4361ee",
    secondary: "#7b5cff",
    success: "#2ecc71",
    warning: "#f39c12",
    error: "#e74c3c",
    info: "#3498db",
    background: "#1a202c",
    card: "#2d3748",
    text: "#f7fafc",
    textSecondary: "#a0aec0",
    columnHeader: "#2d3748",
    columnBackground: "#2d3748",
    columnBackgroundHover: "#3a4556",
    taskBacklog: "#2d3748",
    taskInProgress: "#383245",
    taskDone: "#304035",
    taskBorder: "#4a5568",
    taskShadow: "0 4px 6px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.4)",
    taskShadowHover: "0 10px 15px rgba(0,0,0,0.2), 0 4px 6px rgba(0,0,0,0.3)",
    headerBackground: "#2d3748",
    headerShadow: "0 1px 3px rgba(0,0,0,0.3)",
  };




const Column = ({
  column,
  tasks,
  onOpenAnr,
  moveTask,
  onAddTask,
  onEditTask,
  onDeleteTask = () => {},
  onArchiveTask = () => {},
  onStartTimer = () => {},
  onStopTimer = () => {},
  isDarkMode
}) => {
  const themeColors = isDarkMode ? darkThemeColors : lightThemeColors;
  const [{ isOver }, dropRef] = useDrop({
    accept: ItemTypes.TASK,
    drop: (item) => {
      moveTask(item.task.id, item.from, column.id);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  // Get icon and color based on column type
  let columnIcon, columnColor, columnBgColor;
  switch (column.id) {
    case "backlog":
      columnIcon = <AssignmentIcon />;
      columnColor = "#4361ee";
      columnBgColor = isDarkMode ? "rgba(67, 97, 238, 0.15)" : "#ebf3ff";
      break;
    case "inprogress":
      columnIcon = <AccessTimeIcon />;
      columnColor = "#f39c12";
      columnBgColor = isDarkMode ? "rgba(243, 156, 18, 0.15)" : "#fff8f0";
      break;
    case "done":
      columnIcon = <SaveIcon />;
      columnColor = "#2ecc71";
      columnBgColor = isDarkMode ? "rgba(46, 204, 113, 0.15)" : "#f0fff4";
      break;
    default:
      columnIcon = <DashboardIcon />;
      columnColor = themeColors.primary;
      columnBgColor = isDarkMode ? "rgba(67, 97, 238, 0.15)" : "#ebf3ff";
  }

  return (
    <Paper
      ref={dropRef}
      elevation={isOver ? 3 : isDarkMode ? 0 : 1}
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: isOver
          ? themeColors.columnBackgroundHover
          : themeColors.columnBackground,
        borderRadius: 4,
        overflow: "hidden",
        transition: "all 0.2s ease",
        height: "100%",
        border: isDarkMode ? `1px solid ${themeColors.taskBorder}` : 'none',
      }}>
      <Box
        sx={{
          p: 2.5,
          bgcolor: columnBgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
        }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar 
            sx={{ 
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.8)', 
              color: columnColor,
              width: 34, 
              height: 34 
            }}
          >
            {columnIcon}
          </Avatar>
          <Box sx={{ ml: 1.5 }}>
            <Typography 
              variant='h6' 
              sx={{ 
                fontWeight: 600, 
                color: themeColors.text,
                fontSize: '1.1rem',
              }}
            >
              {column.title}
            </Typography>
            <Typography 
              variant='caption' 
              sx={{ 
                color: themeColors.textSecondary
              }}
            >
              {tasks.length} Aufgaben
            </Typography>
          </Box>
        </Box>

        <Tooltip title='Aufgabe hinzufügen'>
          <IconButton
            size='small'
            onClick={() => onAddTask(column.id)}
            sx={{
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
              color: columnColor,
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
              },
              width: 34,
              height: 34,
            }}>
            <AddIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>

      <Box 
        sx={{ 
          p: 2, 
          flex: 1, 
          overflowY: "auto", 
          minHeight: "60vh",
          backgroundColor: isDarkMode ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.01)',
        }}
      >
        {tasks.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              color: themeColors.textSecondary,
              p: 4,
              borderRadius: 2,
              border: `2px dashed ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)'}`,
              minHeight: '200px',
            }}>
            <Box 
              sx={{ 
                mb: 2, 
                p: 1.5, 
                borderRadius: '50%', 
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
              }}
            >
              {columnIcon}
            </Box>
            <Typography variant='body2' align='center' fontWeight={500}>
              Keine Aufgaben vorhanden
            </Typography>
            <Typography variant='caption' align='center' sx={{ mt: 1, px: 4 }}>
              Klicken Sie auf + um eine neue Aufgabe zu erstellen, oder ziehen Sie Aufgaben hierher.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onAddTask(column.id)}
              sx={{ 
                mt: 2, 
                borderRadius: 2,
                color: columnColor,
                borderColor: columnColor,
                '&:hover': {
                  borderColor: columnColor,
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : `${columnColor}10`,
                }
              }}
            >
              Aufgabe hinzufügen
            </Button>
          </Box>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              columnId={column.id}
              onOpenAnr={onOpenAnr}
              onEditTask={onEditTask}
              onDeleteTask={onDeleteTask}
              onArchiveTask={onArchiveTask}
              onStartTimer={onStartTimer}
              onStopTimer={onStopTimer}
              isDarkMode={isDarkMode}
            />
          ))
        )}
      </Box>
    </Paper>
  );
};





/***************************************
 * ANRModal Component - Redesigned
 ***************************************/
const ANRModal = ({ open, onClose, onSelect, isDarkMode }) => {
  const themeColors = isDarkMode ? darkThemeColors : lightThemeColors;
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestProject, setLatestProject] = useState(null);
  const token = getToken();

  useEffect(() => {
    if (!open) return;

    const fetchProjects = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${base_url}/v1/getAllProjects`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.status === 1) {
          const allProjects = response.data.data;
          if (allProjects.length > 0) {
            const sortedProjects = allProjects.sort(
              (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
            );
            setLatestProject(sortedProjects[0]);
            setProjects(allProjects);
            setFilteredProjects(allProjects);
          } else {
            setError("Keine Projekte gefunden.");
          }
        } else {
          setError("Fehler beim Abruf der Projekte.");
        }
      } catch (err) {
        setError("Fehler beim Abruf der Projekte.");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [open]);

  useEffect(() => {
    const filtered = projects.filter((project) => {
      const matchesANR = project.ANR.toLowerCase().includes(
        searchTerm.toLowerCase(),
      );
      const abzeich = project.Auftragsbezeichnung || "";
      const matchesAuftragsbezeichnung = abzeich
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const createdAt = new Date(project.createdAt);
      const createdYear = createdAt.getFullYear().toString();
      const createdMonth = (createdAt.getMonth() + 1)
        .toString()
        .padStart(2, "0");

      const matchesYear = year ? createdYear === year : true;
      const matchesMonth = month ? createdMonth === month : true;

      return (
        (matchesANR || matchesAuftragsbezeichnung) &&
        matchesYear &&
        matchesMonth
      );
    });
    setFilteredProjects(filtered);
  }, [searchTerm, year, month, projects]);

  const handleSelectProject = (project) => {
    onSelect({
      anr: project.ANR,
      auftragsbezeichnung: project.Auftragsbezeichnung,
      maxLohnaufwand: project.MaxLohnaufwand || 0,
      projectId: project._id,
    });
    onClose();
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "550px",
    bgcolor: themeColors.card,
    boxShadow: 24,
    borderRadius: "16px",
    p: 0,
    overflow: "hidden",
    border: isDarkMode ? `1px solid ${themeColors.taskBorder}` : "none",
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='anr-modal-title'
      aria-describedby='anr-modal-description'>
      <Paper sx={modalStyle} elevation={isDarkMode ? 0 : 5}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: themeColors.primary,
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <Typography variant='h6' component='h2' id='anr-modal-title' sx={{ fontWeight: 600 }}>
            ANR Auswählen
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          <TextField
            variant='outlined'
            margin='normal'
            label='Suche nach ANR oder Bezeichnung'
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              sx: { borderRadius: 2 },
              startAdornment: <SearchIcon sx={{ color: themeColors.textSecondary, mr: 1 }} />,
            }}
          />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                variant='outlined'
                label='Jahr'
                select
                fullWidth
                value={year}
                onChange={(e) => setYear(e.target.value)}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}>
                <MenuItem value=''>Alle Jahre</MenuItem>
                {[
                  ...new Set(
                    projects.map((p) => new Date(p.createdAt).getFullYear()),
                  ),
                ].map((y) => (
                  <MenuItem key={y} value={y.toString()}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                variant='outlined'
                label='Monat'
                select
                fullWidth
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}>
                <MenuItem value=''>Alle Monate</MenuItem>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <MenuItem key={m} value={m.toString().padStart(2, "0")}>
                    {new Date(0, m - 1).toLocaleString("de-DE", {
                      month: "long",
                    })}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <CircularProgress size={30} color="primary" />
            </Box>
          )}

          {error && (
            <Box
              sx={{
                p: 2,
                mt: 2,
                bgcolor: isDarkMode ? "rgba(231, 76, 60, 0.2)" : "#ffebee",
                color: isDarkMode ? "#ff8a80" : "#d32f2f",
                borderRadius: 2,
              }}>
              <Typography>{error}</Typography>
            </Box>
          )}

          {/* Latest Project Quick-Select */}
          {latestProject && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant='subtitle2'
                sx={{ mb: 1, color: themeColors.textSecondary, fontWeight: 600 }}>
                Neuestes Projekt
              </Typography>
              <Button
                variant='contained'
                fullWidth
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  bgcolor: themeColors.success,
                  "&:hover": { bgcolor: "#27ae60" },
                  mb: 1,
                }}
                onClick={() => handleSelectProject(latestProject)}
                startIcon={<BusinessIcon />}>
                <Box
                  sx={{
                    textAlign: "left",
                    width: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  <Typography variant='button'>{latestProject.ANR}</Typography>
                  <Typography variant='caption' sx={{ display: "block" }}>
                    {latestProject.Auftragsbezeichnung || "Keine Bezeichnung"}
                  </Typography>
                </Box>
              </Button>
            </Box>
          )}

          {/* Filtered Results */}
          {filteredProjects.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant='subtitle2'
                sx={{ mb: 1, color: themeColors.textSecondary, fontWeight: 600 }}>
                Projekte ({filteredProjects.length})
              </Typography>
              <Paper
                variant='outlined'
                sx={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  borderRadius: 2,
                  p: 1,
                  backgroundColor: isDarkMode ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)',
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                {filteredProjects.map((project) => (
                  <Button
                    key={project._id}
                    variant='outlined'
                    fullWidth
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      justifyContent: "flex-start",
                      textAlign: "left",
                      borderColor: themeColors.taskBorder,
                      backgroundColor: themeColors.card,
                      color: themeColors.text,
                      "&:hover": {
                        bgcolor: isDarkMode ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                        borderColor: themeColors.primary,
                      },
                    }}
                    onClick={() => handleSelectProject(project)}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant='subtitle2' noWrap>
                        {project.ANR}
                      </Typography>
                      <Typography
                        variant='caption'
                        sx={{ display: "block", color: themeColors.textSecondary }}
                        noWrap>
                        {project.Auftragsbezeichnung || "Keine Bezeichnung"}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Paper>
            </Box>
          )}

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant='outlined'
              onClick={onClose}
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: 2,
                color: isDarkMode ? "white" : themeColors.secondary,
                borderColor: isDarkMode ? "rgba(255,255,255,0.3)" : themeColors.secondary,
                "&:hover": {
                  borderColor: isDarkMode ? "white" : themeColors.secondary,
                  backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(58, 12, 163, 0.05)",
                }
              }}>
              Abbrechen
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};



/***************************************
 * KanbanBoardPage - Project-Specific Version
 ***************************************/
const KanbanANRPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDarkMode, setIsDarkMode] = useState(false);
  const themeColors = isDarkMode ? darkThemeColors : lightThemeColors;
  const navigate = useNavigate();
  
  // Get project ANR from URL parameters if available
  const { projectId, anr } = useParams();
  const location = useLocation();
  const projectDetails = location.state?.projectDetails || null;
  const isProjectSpecific = Boolean(projectId || anr);
  
  // Default project info
  const [projectInfo, setProjectInfo] = useState({
    anr: anr || (projectDetails ? projectDetails.ANR : ""),
    name: projectDetails ? projectDetails.Auftragsbezeichnung : "",
    maxHours: projectDetails ? projectDetails.MaxLohnaufwand || 0 : 0,
    id: projectId || (projectDetails ? projectDetails._id : ""),
  });
  
  // Columns
  const [columns, setColumns] = useState([
    { id: "backlog", title: "Backlog" },
    { id: "inprogress", title: "In Progress" },
    { id: "done", title: "Done" },
  ]);
  
  const user = useSelector((state) => state.user);
  const username = user?.user?.firstName + " " + user?.user?.lastName;

  // Tasks
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Employees list
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // ANR assignment
  const [anrModalOpen, setAnrModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  // Load tasks from API - This would now include filter by project if needed
  useEffect(() => {
    const fetchTasks = async () => {
      setIsLoading(true);
      try {
        // Determine the endpoint based on whether we're in project-specific mode
        let endpoint = `${base_url}/v1/getAufgabeById?id=${projectInfo.id}`;
        
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        
        if (response.data && Array.isArray(response.data)) {
          // Transform API data to match our task structure
          const transformedTasks = response.data.map(task => ({
            id: task._id,
            title: task.title,
            creator: task.createdBy?.name || username,
            description: task.description || "",
            hours: task.hours || 0,
            anr: task.projectId?.ANR || (isProjectSpecific ? projectInfo.anr : ""),
            column: task.status === "done" ? "done" : 
                   task.status === "inprogress" ? "inprogress" : "backlog",
            documents: task.documents || [],
            assignedTo: task.user ? {
              _id: task.user._id,
              name: `${task.user.firstName} ${task.user.lastName}`
            } : null,
            assignedDate: task.assignedDate,
            deadline: task.deadline,
          }));
          
          setTasks(transformedTasks);
        } else {
          // If in project-specific mode but no tasks found, create a sample one
          if (isProjectSpecific) {
            setTasks([{
              id: uuidv4(),
              title: "Erste Aufgabe für " + projectInfo.anr,
              creator: username,
              description: "Dies ist die erste Aufgabe für dieses Projekt. Fügen Sie Details hinzu.",
              hours: 0,
              anr: projectInfo.anr,
              column: "backlog",
              documents: [],
              assignedTo: null,
              assignedDate: null,
              deadline: null,
            }]);
          } else {
            // Default tasks for general kanban
            setTasks([
              {
                id: uuidv4(),
                title: "Task A",
                creator: username,
                description: "Erste Aufgabe im Backlog",
                hours: 3,
                anr: "",
                column: "backlog",
                documents: [],
                assignedTo: null,
                assignedDate: null,
                deadline: null,
              },
              {
                id: uuidv4(),
                title: "Task B",
                creator: username,
                description: "Zweite Aufgabe in Arbeit",
                hours: 5,
                anr: "",
                column: "inprogress",
                documents: [],
                assignedTo: null,
                assignedDate: null,
                deadline: null,
              },
              {
                id: uuidv4(),
                title: "Task C",
                creator: username,
                description: "Abgeschlossene Aufgabe",
                hours: 2,
                anr: "",
                column: "done",
                documents: [],
                assignedTo: null,
                assignedDate: null,
                deadline: null,
              },
            ]);
          }
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
        toast.error("Fehler beim Laden der Aufgaben");
        
        // Set default tasks in case of error
        setTasks([
          {
            id: uuidv4(),
            title: "Task A",
            creator: username,
            description: "Erste Aufgabe im Backlog",
            hours: 3,
            anr: isProjectSpecific ? projectInfo.anr : "",
            column: "backlog",
            documents: [],
            assignedTo: null,
            assignedDate: null,
            deadline: null,
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTasks();
  }, [projectInfo.id, projectInfo.anr, isProjectSpecific, username]);

  // Load project details if in project-specific mode but we only have an ID
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (isProjectSpecific && projectId && !projectInfo.anr) {
        try {
          const response = await axios.get(`${base_url}/v1/getProject/${projectId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          
          if (response.data && response.data.status === 1) {
            const project = response.data.data;
            setProjectInfo({
              anr: project.ANR,
              name: project.Auftragsbezeichnung,
              maxHours: project.MaxLohnaufwand || 0,
              id: project._id,
            });
          }
        } catch (error) {
          console.error("Error fetching project details:", error);
          toast.error("Fehler beim Laden der Projektdetails");
        }
      }
    };
    
    fetchProjectDetails();
  }, [projectId, isProjectSpecific, projectInfo.anr]);

  // Load employees list
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const response = await axios.get(`${base_url}/v1/getAllEmployees`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (response.data && Array.isArray(response.data)) {
          setEmployees(response.data);
        } else {
          console.error("Unexpected employee data format:", response.data);
          toast.error("Fehler beim Laden der Mitarbeiterliste");
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Fehler beim Laden der Mitarbeiterliste");
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, []);

  // Task form
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [IsEdit, setIsEdit] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentColumnId, setCurrentColumnId] = useState(null);

  // Move task
  const moveTask = (taskId, fromColId, toColId) => {
    if (fromColId === toColId) return;

    setTasks((prevTasks) =>
      prevTasks.map((t) => (t.id === taskId ? { ...t, column: toColId } : t)),
    );

    // Create API request to update task status
    const updateTaskStatus = async () => {
      try {
        const task = tasks.find(t => t.id === taskId);
        if (!task) return;
        
        // Map our column IDs to status values used by the API
        const statusMap = {
          'backlog': 'assigned',
          'inprogress': 'inprogress',
          'done': 'done'
        };
        
        await axios.put(
          `${base_url}/v1/updateAufgabe/${taskId}`,
          { status: statusMap[toColId] },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      } catch (error) {
        console.error("Error updating task status:", error);
        // We don't revert the UI change even if API call fails to avoid confusion
      }
    };
    
    updateTaskStatus();

    // Show success toast
    toast.success(
      `Aufgabe nach "${
        columns.find((c) => c.id === toColId).title
      }" verschoben.`,
      {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: isDarkMode ? "dark" : "light",
      }
    );
  };

  // Open ANR Modal
  const handleOpenAnr = (taskId) => {
    // If in project-specific mode, ANR is already fixed
    if (isProjectSpecific) {
      // Just update the task with the project ANR
      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === taskId
            ? {
                ...t,
                anr: projectInfo.anr,
              }
            : t,
        ),
      );
      toast.success("ANR automatisch zugewiesen!", {
        position: "bottom-right",
        autoClose: 2000,
        theme: isDarkMode ? "dark" : "light",
      });
    } else {
      // For general kanban, open the ANR selection modal
      setSelectedTaskId(taskId);
      setAnrModalOpen(true);
    }
  };

  // ANR selection
  const handleAnrSelect = (anrData) => {
    setTasks((prevTasks) =>
      prevTasks.map((t) =>
        t.id === selectedTaskId
          ? {
              ...t,
              anr: anrData.anr,
            }
          : t,
      ),
    );
    setSelectedTaskId(null);
    toast.success("ANR erfolgreich zugewiesen!", {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: isDarkMode ? "dark" : "light",
    });
  };

  // Open task form
  const handleAddTask = (columnId) => {
    setCurrentTask(null);
    setCurrentColumnId(columnId);
    
    // For project-specific tasks, we'll pre-fill the ANR
    if (isProjectSpecific) {
      setCurrentTask({
        title: "",
        creator: username,
        description: "",
        hours: 0,
        anr: projectInfo.anr,
        files: [],
        assignedTo: null,
        assignedDate: new Date(),
        deadline: null,
      });
    }
    
    setTaskFormOpen(true);
    setIsEdit(false);
  };

  // Edit task
  const handleEditTask = (task) => {
    setCurrentTask(task);
    setCurrentColumnId(task.column);
    setTaskFormOpen(true);
    setIsEdit(true);
  };
  
  // Delete Task
  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${base_url}/v1/deleteAufgabe/${taskId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.info("Aufgabe gelöscht");
    } catch (err) {
      console.error("Error deleting task:", err);
      toast.error("Fehler beim Löschen der Aufgabe");
    }
  };
  
  // Archive Task
  const handleArchiveTask = async (taskId) => {
    try {
      await axios.put(`${base_url}/v1/archiveAufgabe/${taskId}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      
      // Remove task from the board
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.info("Aufgabe archiviert");
    } catch (err) {
      console.error("Error archiving task:", err);
      toast.error("Fehler beim Archivieren der Aufgabe");
    }
  };
  
  // Start Timer
  const handleStartTimer = async (taskId) => {
    try {
      const response = await axios.put(`${base_url}/v1/startTaskTimer/${taskId}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      
      if (response.data && response.data.task) {
        // Update the task in the state with the new data
        setTasks((prev) => 
          prev.map((t) => 
            t.id === taskId 
              ? { 
                  ...t, 
                  startTime: response.data.task.startTime,
                  status: response.data.task.status,
                  column: "inprogress" // Also update the column to maintain UI consistency
                } 
              : t
          )
        );
        toast.success("Timer gestartet");
      }
    } catch (err) {
      console.error("Error starting timer:", err);
      toast.error("Fehler beim Starten des Timers");
    }
  };
  
  // Stop Timer
  const handleStopTimer = async (taskId) => {
    try {
      const response = await axios.put(`${base_url}/v1/stopTaskTimer/${taskId}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      
      if (response.data && response.data.task) {
        // Update the task in the state with the new data
        setTasks((prev) => 
          prev.map((t) => 
            t.id === taskId 
              ? { 
                  ...t, 
                  endTime: response.data.task.endTime
                } 
              : t
          )
        );
        toast.success("Timer gestoppt");
      }
    } catch (err) {
      console.error("Error stopping timer:", err);
      toast.error("Fehler beim Stoppen des Timers");
    }
  };

  // Save task
  const handleSaveTask = async (task) => {
    try {
      // Prepare data for API call
      const formData = new FormData();
      formData.append("title", task.title);
      formData.append("description", task.description || "");
      formData.append("hours", task.hours || 0);
      
      // Map our column IDs to status values used by the API
      const statusMap = {
        'backlog': 'assigned',
        'inprogress': 'inprogress',
        'done': 'done'
      };
      formData.append("status", statusMap[task.column]);
      
      // Add user assignment if available
      if (task.assignedTo && task.assignedTo._id) {
        formData.append("user", task.assignedTo._id);
      }
      
      // Add project reference if available
      if (isProjectSpecific && projectInfo.id) {
        formData.append("projectId", projectInfo.id);
      } else if (task.anr) {
        // Try to find the project ID based on ANR (simplified)
        // In a real implementation, you might want to look up the project ID from the ANR
        formData.append("anr", task.anr);
      }
      
      // Add deadlines if set
      if (task.deadline) {
        formData.append("deadline", task.deadline.toISOString());
      }
      
      // Add document files if any
      if (task.files && task.files.length > 0) {
        task.files.forEach(file => {
          formData.append("documents", file);
        });
      }
      
      if (IsEdit) {
        // Update existing task
        await axios.put(
          `${base_url}/v1/updateAufgabe/${task.id}`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${getToken()}`,
              "Content-Type": "multipart/form-data"
            } 
          }
        );
        
        setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
        toast.success("Aufgabe aktualisiert!", {
          position: "bottom-right",
          autoClose: 2000,
          theme: isDarkMode ? "dark" : "light",
        });
      } else {
        // Create new task
        const response = await axios.post(
          `${base_url}/v1/createAufgabe`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${getToken()}`,
              "Content-Type": "multipart/form-data"
            } 
          }
        );
        
        // If successful, add the task with the ID from the server
        if (response.data && response.data._id) {
          const newTask = {
            ...task,
            id: response.data._id,
          };
          setTasks((prev) => [...prev, newTask]);
        } else {
          // Fallback if server doesn't return ID
          setTasks((prev) => [...prev, task]);
        }
        
        toast.success("Neue Aufgabe erstellt!", {
          position: "bottom-right",
          autoClose: 2000,
          theme: isDarkMode ? "dark" : "light",
        });
      }
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Fehler beim Speichern der Aufgabe", {
        position: "bottom-right",
        autoClose: 3000,
        theme: isDarkMode ? "dark" : "light",
      });
      
      // Still update UI to avoid confusion
      if (IsEdit) {
        setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
      } else {
        setTasks((prev) => [...prev, task]);
      }
    }
  };

  // Save board
  const handleSaveBoard = async () => {
    try {
      // This would typically make bulk updates to the server
      toast.success("Board erfolgreich gespeichert!", {
        position: "bottom-right",
        autoClose: 2000,
        theme: isDarkMode ? "dark" : "light",
      });
    } catch (err) {
      toast.error("Fehler beim Speichern des Boards", {
        position: "bottom-right",
        autoClose: 3000,
        theme: isDarkMode ? "dark" : "light",
      });
    }
  };

  // Toggle theme
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Go back to projects list
  const handleBackToProjects = () => {
    navigate('/projects');
  };

  // Calculate statistics
  const totalTasks = tasks.length;
  const tasksWithAnr = tasks.filter(t => t.anr && t.anr.trim() !== "").length;
  const totalHours = tasks.reduce((sum, t) => sum + (t.hours || 0), 0);
  const selfAssignedTasks = tasks.filter(t => t.assignedTo && t.assignedTo._id === user?.user?._id).length;
  const usedHoursPercentage = isProjectSpecific && projectInfo.maxHours > 0 
    ? Math.min(100, (totalHours / projectInfo.maxHours) * 100) 
    : 0;

  // Filter tasks if search is implemented
  const [searchTerm, setSearchTerm] = useState("");
  const filteredTasks = tasks.filter(task => 
    task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.anr && task.anr.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          bgcolor: isDarkMode ? darkThemeColors.background : lightThemeColors.background,
          transition: 'background-color 0.3s ease',
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3, color: isDarkMode ? darkThemeColors.primary : lightThemeColors.primary }} />
        <Typography variant="h6" color={isDarkMode ? darkThemeColors.text : lightThemeColors.text}>
          Laden des Kanban Boards...
        </Typography>
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          bgcolor: themeColors.background,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          transition: "background-color 0.3s ease",
        }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: themeColors.headerBackground,
            boxShadow: themeColors.headerShadow,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isProjectSpecific && (
              <IconButton 
                onClick={handleBackToProjects}
                sx={{ mr: 1 }}
                aria-label="Zurück zur Projektliste"
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <DashboardIcon 
              sx={{ 
                fontSize: 28, 
                color: themeColors.primary, 
                mr: 1.5 
              }} 
            />
            <Box>
              <Typography
                variant='h5'
                fontWeight='bold'
                color={themeColors.text}>
                {isProjectSpecific ? "Projekt Kanban" : "Kanban Board"}
              </Typography>
              
              {isProjectSpecific && (
                <Breadcrumbs 
                  separator={<NavigateNextIcon fontSize="small" />}
                  aria-label="breadcrumb"
                >
                  <Link 
                    color={themeColors.textSecondary}
                    href="/projects"
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                    Projekte
                  </Link>
                  <Typography color={themeColors.primary} sx={{ fontWeight: 500 }}>
                    {projectInfo.anr}
                  </Typography>
                </Breadcrumbs>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title={isDarkMode ? "Light Mode" : "Dark Mode"}>
              <IconButton onClick={toggleTheme} sx={{ mr: 1 }}>
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Benachrichtigungen">
              <IconButton sx={{ mr: 1 }}>
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <Avatar 
              sx={{ 
                bgcolor: themeColors.primary,
                width: 36,
                height: 36,
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}
            >
              {username.charAt(0)}
            </Avatar>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Project Info (only shown in project-specific mode) */}
          {isProjectSpecific && (
            <Paper
              elevation={isDarkMode ? 0 : 2}
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 3,
                backgroundColor: themeColors.card,
                border: isDarkMode ? `1px solid ${themeColors.taskBorder}` : 'none',
              }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BusinessIcon sx={{ color: themeColors.primary, mr: 1.5, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={600} color={themeColors.text} sx={{ mb: 0.5 }}>
                      {projectInfo.anr}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color={themeColors.text} sx={{ mb: 2 }}>
                    {projectInfo.name || "Projektbezeichnung nicht verfügbar"}
                  </Typography>
                </Box>
                {projectInfo.maxHours > 0 && (
                  <Box sx={{ width: '200px', textAlign: 'right' }}>
                    <Typography variant="body2" color={themeColors.textSecondary} sx={{ mb: 0.5 }}>
                      Stundenkontingent: {totalHours} / {projectInfo.maxHours} Stunden
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={usedHoursPercentage} 
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        mb: 0.5,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: usedHoursPercentage > 90 ? themeColors.error : 
                                          usedHoursPercentage > 70 ? themeColors.warning : 
                                          themeColors.success
                        }
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: usedHoursPercentage > 90 ? themeColors.error : 
                              usedHoursPercentage > 70 ? themeColors.warning : 
                              themeColors.success,
                        fontWeight: 500
                      }}
                    >
                      {usedHoursPercentage.toFixed(0)}% verwendet
                    </Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)', my: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  sx={{ 
                    borderRadius: 2,
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : themeColors.taskBorder,
                    color: themeColors.text,
                    '&:hover': {
                      borderColor: themeColors.primary,
                      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.01)'
                    }
                  }}
                  onClick={() => {
                    // Refresh tasks
                    window.location.reload();
                  }}
                >
                  Aktualisieren
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: themeColors.primary,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#3651d1',
                      boxShadow: 'none',
                    }
                  }}
                  onClick={() => handleAddTask("backlog")}
                >
                  Neue Aufgabe
                </Button>
              </Box>
            </Paper>
          )}

          {/* Stats & Filters */}
          <Paper
            elevation={isDarkMode ? 0 : 2}
            sx={{
              p: 2.5,
              mb: 4,
              borderRadius: 3,
              backgroundColor: themeColors.card,
              border: isDarkMode ? `1px solid ${themeColors.taskBorder}` : 'none',
            }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={600} color={themeColors.text} sx={{ mb: 2 }}>
                Dashboard Übersicht
              </Typography>
              <Divider sx={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)', mb: 2 }} />
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  backgroundColor: isDarkMode ? 'rgba(67, 97, 238, 0.15)' : '#ebf3ff',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box sx={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: '12px', 
                    bgcolor: isDarkMode ? 'rgba(67, 97, 238, 0.3)' : 'rgba(67, 97, 238, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                  <AssignmentIcon sx={{ color: themeColors.primary, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color={themeColors.text}>
                    {totalTasks}
                  </Typography>
                  <Typography variant="body2" color={themeColors.textSecondary}>
                    Aufgaben gesamt
                  </Typography>
                </Box>
              </Box>
            </Grid>
              
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDarkMode ? 'rgba(46, 204, 113, 0.15)' : '#f0fff4',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  bgcolor: isDarkMode ? 'rgba(46, 204, 113, 0.3)' : 'rgba(46, 204, 113, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <BusinessIcon sx={{ color: themeColors.success, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color={themeColors.text}>
                    {tasksWithAnr}
                  </Typography>
                  <Typography variant="body2" color={themeColors.textSecondary}>
                    Zugewiesene ANRs
                  </Typography>
                </Box>
              </Box>
            </Grid>
              
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDarkMode ? 'rgba(243, 156, 18, 0.15)' : '#fff8f0',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  bgcolor: isDarkMode ? 'rgba(243, 156, 18, 0.3)' : 'rgba(243, 156, 18, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <AccessTimeIcon sx={{ color: themeColors.warning, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color={themeColors.text}>
                    {totalHours}
                  </Typography>
                  <Typography variant="body2" color={themeColors.textSecondary}>
                    Gesamtstunden
                  </Typography>
                </Box>
              </Box>
            </Grid>
              
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: isDarkMode ? 'rgba(52, 152, 219, 0.15)' : '#e3f2fd',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '12px', 
                  bgcolor: isDarkMode ? 'rgba(52, 152, 219, 0.3)' : 'rgba(52, 152, 219, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <PersonIcon sx={{ color: themeColors.info, fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color={themeColors.text}>
                    {selfAssignedTasks}
                  </Typography>
                  <Typography variant="body2" color={themeColors.textSecondary}>
                    Selbst zugewiesen
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
            
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                placeholder="Suche nach Aufgaben..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  sx: { borderRadius: 2 }
                }}
                sx={{ mr: 2, width: 250 }}
              />
                
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                sx={{
                  borderRadius: 2,
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : themeColors.taskBorder,
                  color: themeColors.textSecondary,
                  '&:hover': {
                    borderColor: themeColors.primary,
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.01)'
                  }
                }}>
                Filter
              </Button>
            </Box>
              
            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                sx={{ 
                  mr: 2, 
                  borderRadius: 2,
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : themeColors.taskBorder,
                  color: themeColors.text,
                  '&:hover': {
                    borderColor: themeColors.primary,
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.01)'
                  }
                }}
                onClick={() => toast.info("Daten aktualisiert", {
                  position: "bottom-right",
                  autoClose: 2000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  theme: isDarkMode ? "dark" : "light",
                })}>
                Aktualisieren
              </Button>

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveBoard}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: themeColors.primary,
                  '&:hover': {
                    backgroundColor: '#3651d1',
                    boxShadow: 'none',
                  }
                }}>
                Speichern
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Kanban Columns */}
        <Grid 
          container 
          spacing={3} 
          sx={{ 
            mb: 10,
            transition: 'all 0.3s ease' 
          }}
        >
          {columns.map((col) => (
            <Grid item xs={12} md={4} key={col.id}>
              <Column
                column={col}
                tasks={filteredTasks.filter((task) => task.column === col.id)}
                onOpenAnr={handleOpenAnr}
                moveTask={moveTask}
                onAddTask={handleAddTask}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                onArchiveTask={handleArchiveTask}
                onStartTimer={handleStartTimer}
                onStopTimer={handleStopTimer}
                isDarkMode={isDarkMode}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Quick Add Button - Fixed */}
      <Tooltip title='Schnell Aufgabe hinzufügen'>
        <Button
          variant='contained'
          color='primary'
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            borderRadius: "50%",
            width: 60,
            height: 60,
            boxShadow: '0 4px 10px rgba(67, 97, 238, 0.3)',
            backgroundColor: themeColors.primary,
            '&:hover': {
              backgroundColor: '#3651d1',
              boxShadow: '0 6px 15px rgba(67, 97, 238, 0.4)',
            }
          }}
          onClick={() => handleAddTask("backlog")}>
          <AddIcon fontSize='large' />
        </Button>
      </Tooltip>

      {/* ANR Modal for tasks - Only used in general Kanban mode */}
      {!isProjectSpecific && (
        <ANRModal
          open={anrModalOpen}
          onClose={() => setAnrModalOpen(false)}
          onSelect={handleAnrSelect}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Task Form Modal */}
      <TaskFormModal
        open={taskFormOpen}
        onClose={() => setTaskFormOpen(false)}
        onSave={handleSaveTask}
        initialTask={currentTask}
        columnId={currentColumnId}
        employees={employees}
        isDarkMode={isDarkMode}
        isProjectSpecific={isProjectSpecific}
        projectInfo={projectInfo}
      />

      <ToastContainer
        position='bottom-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </Box>
  </DndProvider>
  );
};

export default KanbanANRPage;